/**
 * Postgres v-next observability storage domain.
 *
 * Insert-only model. Mirrors the ClickHouse v-next layout but adapted for
 * Postgres semantics:
 *   - per-signal partitioned tables (or Timescale hypertables when the
 *     extension is detected)
 *   - retry idempotency via `ON CONFLICT DO NOTHING` on the partition-aware
 *     primary key (the ClickHouse design uses ReplacingMergeTree dedupeKey)
 *   - root-span reads served by partial indexes on the span events table
 *   - discovery values cached in a Postgres table with stale-while-revalidate
 *     semantics, so cache state survives serverless restarts and works
 *     across multiple frontends pointing at the same DB
 *
 * IMPORTANT: this domain is intended for **low-volume production** workloads
 * only. Customers running more than ~100 calls/sec sustained should use the
 * ClickHouse adapter. See `observability/postgres-design/recommendation.md`
 * for the volume math behind this guidance.
 *
 * The adapter should NOT share a database with the customer's primary
 * application database — observability writes will degrade app performance.
 * Use it through `MastraCompositeStore` with a dedicated Postgres connection.
 */

import { ErrorCategory, ErrorDomain, MastraError } from '@mastra/core/error';
import * as coreStorage from '@mastra/core/storage';
import { createStorageErrorId, ObservabilityStorage } from '@mastra/core/storage';
import type {
  BatchCreateFeedbackArgs,
  BatchCreateLogsArgs,
  BatchCreateMetricsArgs,
  BatchCreateScoresArgs,
  BatchCreateSpansArgs,
  BatchDeleteTracesArgs,
  CreateFeedbackArgs,
  CreateScoreArgs,
  DeleteFeedbackArgs,
  DeleteScoresArgs,
  CreateSpanArgs,
  GetEntityNamesArgs,
  GetEntityNamesResponse,
  GetEntityTypesArgs,
  GetEntityTypesResponse,
  GetEnvironmentsArgs,
  GetEnvironmentsResponse,
  GetFeedbackAggregateArgs,
  GetFeedbackAggregateResponse,
  GetFeedbackBreakdownArgs,
  GetFeedbackBreakdownResponse,
  GetFeedbackPercentilesArgs,
  GetFeedbackPercentilesResponse,
  GetFeedbackTimeSeriesArgs,
  GetFeedbackTimeSeriesResponse,
  GetMetricAggregateArgs,
  GetMetricAggregateResponse,
  GetMetricBreakdownArgs,
  GetMetricBreakdownResponse,
  GetMetricLabelKeysArgs,
  GetMetricLabelKeysResponse,
  GetMetricLabelValuesArgs,
  GetMetricLabelValuesResponse,
  GetMetricNamesArgs,
  GetMetricNamesResponse,
  GetMetricPercentilesArgs,
  GetMetricPercentilesResponse,
  GetMetricTimeSeriesArgs,
  GetMetricTimeSeriesResponse,
  GetRootSpanArgs,
  GetRootSpanResponse,
  GetSpansArgs,
  GetSpansResponse,
  GetScoreAggregateArgs,
  GetScoreAggregateResponse,
  GetScoreBreakdownArgs,
  GetScoreBreakdownResponse,
  GetScorePercentilesArgs,
  GetScorePercentilesResponse,
  GetScoreTimeSeriesArgs,
  GetScoreTimeSeriesResponse,
  GetServiceNamesArgs,
  GetServiceNamesResponse,
  GetSpanArgs,
  GetSpanResponse,
  GetTagsArgs,
  GetTagsResponse,
  GetTraceArgs,
  GetTraceLightResponse,
  GetTraceResponse,
  ListBranchesArgs,
  ListBranchesResponse,
  ListFeedbackArgs,
  ListFeedbackResponse,
  FeedbackRecord,
  UpdateFeedbackReviewStatusArgs,
  ListLogsArgs,
  ListLogsResponse,
  ListMetricsArgs,
  ListMetricsResponse,
  ListScoresArgs,
  ListScoresResponse,
  ListTracesArgs,
  ListTracesResponse,
  ObservabilityStorageStrategy,
  PruneOptions,
  PruneResult,
  RetentionTablesDescriptor,
  QueryThreadsResult,
  ScoreRecord,
  TableRetentionPolicy,
  TraceQueryResponse,
  TrustedThreadQueryPlan,
  TrustedTraceQueryPlan,
} from '@mastra/core/storage';

import type { DbClient } from '../../../client';
import { resolvePgConfig } from '../../../db';
import type { PgDomainConfig } from '../../../db';
import {
  ALL_SIGNAL_TABLES,
  additiveColumns,
  allIndexDDL,
  allTableDDL,
  columnExistsSQL,
  qualifiedTable,
  schemaDDL,
  TABLE_DISCOVERY,
  TABLE_FEEDBACK_EVENTS,
  TABLE_LOG_EVENTS,
  TABLE_METRIC_EVENTS,
  TABLE_SCORE_EVENTS,
  TABLE_SPAN_EVENTS,
} from './ddl';
import * as discoveryOps from './discovery';
import type { DiscoveryConfig } from './discovery';
import * as feedbackOps from './feedback';
import * as logsOps from './logs';
import * as metricsOps from './metrics';
import { detectPartman, detectTimescale, resolveMode, setupPartitioning } from './partitioning';
import type { PartitioningOptions, PartitionMode } from './partitioning';
import { isDuplicateRelationError, isDuplicateSchemaError } from './pg-errors';
import { deltaPollingFeatureEnabled } from './polling';
import { prunePartitionedTable, pruneTimescaleTable, retentionCutoff } from './retention';
import * as scoresOps from './scores';
import * as traceQueryOps from './trace-query';
import * as tracesOps from './traces';
import * as tracingOps from './tracing';

export type { PartitionMode, PartitioningOptions } from './partitioning';
export type { DiscoveryConfig } from './discovery';

/** Configuration for the v-next Postgres observability domain. */
export type VNextPostgresObservabilityConfig = PgDomainConfig & {
  /** Daily-partition / Timescale hypertable behavior. Default 'auto'. */
  partitioning?: PartitioningOptions;
  /** Discovery cache configuration. */
  discovery?: DiscoveryConfig;
  /** Maximum execution time for one advanced trace query. Default 15 seconds. */
  traceQueryTimeoutMs?: number;
};

function wrapError(op: string, error: unknown, details?: Record<string, unknown>): never {
  if (error instanceof MastraError || error instanceof coreStorage.TraceQueryExecutionError) throw error;
  throw new MastraError(
    {
      id: createStorageErrorId('PG', op, 'FAILED'),
      domain: ErrorDomain.STORAGE,
      category: ErrorCategory.THIRD_PARTY,
      details: details as Record<string, any>,
    },
    error,
  );
}

export class ObservabilityStoragePostgresVNext extends ObservabilityStorage {
  readonly #client: DbClient;
  /** Reader-backed client for standalone reads; writes, DDL, and discovery-cache refresh stay on #client. */
  readonly #readClient: DbClient;
  readonly #schema: string;
  readonly #partitioning: PartitioningOptions;
  readonly #discoveryConfig: DiscoveryConfig;
  readonly #traceQueryTimeoutMs: number;
  #partitionMode?: PartitionMode;

  constructor(config: VNextPostgresObservabilityConfig) {
    super();
    const { client, readClient, schemaName } = resolvePgConfig(config);
    this.#client = client;
    this.#readClient = readClient;
    this.#schema = schemaName ?? 'public';
    this.#partitioning = config.partitioning ?? {};
    this.#discoveryConfig = config.discovery ?? {};
    this.#traceQueryTimeoutMs = coreStorage.resolveTraceQueryTimeoutMs(config.traceQueryTimeoutMs);
  }

  /**
   * Build the discovery config used at each call site, with the framework
   * logger injected so background refresh failures land in the same log
   * stream as the rest of the store. Reads `this.logger` lazily so
   * `__setLogger()` calls (e.g. when the domain is mounted under a Mastra
   * instance) propagate without rebuilding the domain.
   */
  get #discovery(): DiscoveryConfig {
    return { ...this.#discoveryConfig, logger: this.logger };
  }

  // -------------------------------------------------------------------------
  // Initialization
  // -------------------------------------------------------------------------

  /**
   * Create the signal tables, indexes, and (if Timescale / pg_partman is
   * present) hypertable / partman registrations.
   *
   * Not transactional: each `CREATE TABLE IF NOT EXISTS`, `CREATE INDEX IF
   * NOT EXISTS`, and `create_hypertable()` / `create_parent()` runs in its
   * own implicit transaction. Re-running `init()` after a failure is safe
   * (every statement is idempotent), but a failure partway through against
   * Timescale can leave some signal tables as hypertables and others as
   * plain tables. If that happens, fix the underlying error and call
   * `init()` again — the partially-converted state is recoverable.
   */
  async init(): Promise<void> {
    try {
      const explicit = this.#partitioning.mode;
      let mode: PartitionMode;
      if (explicit && explicit !== 'auto') {
        mode = explicit;
      } else if (await detectTimescale(this.#client)) {
        mode = 'timescale';
      } else if (await detectPartman(this.#client)) {
        mode = 'partman';
      } else {
        mode = 'native';
      }

      const ddlMode = mode === 'timescale' ? 'timescale' : 'partitioned';

      try {
        await this.#client.none(schemaDDL(this.#schema));
      } catch (error) {
        // `CREATE SCHEMA IF NOT EXISTS` is not atomic; two concurrent
        // init() calls against a fresh schema can both pass the
        // existence probe and one will surface 42P06 or a 23505 on
        // pg_namespace_nspname_index. Treat it as success.
        if (!isDuplicateSchemaError(error)) throw error;
      }

      for (const ddl of allTableDDL(this.#schema, ddlMode)) {
        try {
          await this.#client.none(ddl);
        } catch (error) {
          if (!isDuplicateRelationError(error)) throw error;
        }
      }
      // Additive column migrations for tables created by an older version of
      // this schema. Runs before index creation so indexes can reference new
      // columns. The ALTER is skipped unless the column is actually missing —
      // it locks the partitioned parent, and init() runs on every boot.
      for (const { table, column, ddl } of additiveColumns(this.#schema)) {
        const present = await this.#client.oneOrNone(columnExistsSQL, [this.#schema, table, column]);
        if (!present) {
          await this.#client.none(ddl);
        }
      }
      for (const ddl of allIndexDDL(this.#schema)) {
        try {
          await this.#client.none(ddl);
        } catch (error) {
          if (!isDuplicateRelationError(error)) throw error;
        }
      }

      this.#partitionMode = await setupPartitioning(this.#client, this.#schema, {
        ...this.#partitioning,
        mode,
      });
    } catch (error) {
      wrapError('VNEXT_INIT', error);
    }
  }

  /** Resolved partition mode after init(). Useful for tests and diagnostics. */
  get partitionMode(): PartitionMode | undefined {
    return this.#partitionMode;
  }

  // -------------------------------------------------------------------------
  // Retention
  // -------------------------------------------------------------------------

  /**
   * All five signal tables are insert-only growth tables. The anchor column is
   * each table's partition / chunk key, so age-based expiry drops whole day
   * partitions instead of deleting rows (see `./retention.ts`). `indexed: true`
   * reflects that expiry never scans — it acts on partition bounds.
   */
  static override readonly retentionTables: RetentionTablesDescriptor = {
    spans: { table: TABLE_SPAN_EVENTS, column: 'endedAt', indexed: true },
    metrics: { table: TABLE_METRIC_EVENTS, column: 'timestamp', indexed: true },
    logs: { table: TABLE_LOG_EVENTS, column: 'timestamp', indexed: true },
    scores: { table: TABLE_SCORE_EVENTS, column: 'timestamp', indexed: true },
    feedback: { table: TABLE_FEEDBACK_EVENTS, column: 'timestamp', indexed: true },
  };

  /**
   * Expire signal events older than each table's `maxAge` by dropping whole
   * day partitions (native / pg_partman) or chunks (Timescale). Only
   * partitions wholly older than the cutoff are dropped, so the effective
   * granularity is one day. Each partition drop counts as one batch for
   * `maxBatches` / `maxRows` / abort-signal purposes; `deleted` reports the
   * row count of the dropped partitions.
   */
  override async prune(policies: Record<string, TableRetentionPolicy>, options?: PruneOptions): Promise<PruneResult[]> {
    return this.#run('VNEXT_PRUNE', async () => {
      // Resolve lazily so prune() works on a domain that was constructed but
      // not init()-ed in this process (e.g. a dedicated cron worker).
      const mode = (this.#partitionMode ??= await resolveMode(this.#client, this.#partitioning));

      const results: PruneResult[] = [];
      const now = Date.now();

      for (const [key, entry] of Object.entries(ObservabilityStoragePostgresVNext.retentionTables)) {
        const policy = policies[key];
        if (!policy) continue;

        if (options?.signal?.aborted) {
          results.push({ domain: 'observability', table: entry.table, deleted: 0, done: false });
          continue;
        }

        const cutoff = retentionCutoff(policy, now);
        const args = { client: this.#client, schema: this.#schema, table: entry.table, cutoff, options };
        const outcome = mode === 'timescale' ? await pruneTimescaleTable(args) : await prunePartitionedTable(args);

        results.push({ domain: 'observability', table: entry.table, deleted: outcome.deleted, done: outcome.done });
      }

      return results;
    });
  }

  public override get observabilityStrategy(): {
    preferred: ObservabilityStorageStrategy;
    supported: ObservabilityStorageStrategy[];
  } {
    // Event-sourced rather than insert-only: the exporter also emits a row when
    // a span starts, which is what makes a run visible in Studio while it is
    // still executing. Reads collapse a span's rows back to one record.
    return { preferred: 'event-sourced', supported: ['event-sourced'] };
  }

  override getFeatures() {
    if (!deltaPollingFeatureEnabled()) return ['metrics', 'logs', 'trace-query', 'thread-query'] as const;
    return ['metrics', 'logs', 'delta-polling', 'trace-query', 'thread-query'] as const;
  }

  async #run<T>(op: string, fn: () => Promise<T>, details?: Record<string, unknown>): Promise<T> {
    try {
      return await fn();
    } catch (error) {
      wrapError(op, error, details);
    }
  }

  // -------------------------------------------------------------------------
  // Tracing — writes
  // -------------------------------------------------------------------------

  override async createSpan(args: CreateSpanArgs): Promise<void> {
    await this.#run('CREATE_SPAN', () => tracingOps.createSpan(this.#client, this.#schema, args), {
      traceId: args.span.traceId,
      spanId: args.span.spanId,
    });
  }

  override async batchCreateSpans(args: BatchCreateSpansArgs): Promise<void> {
    await this.#run('BATCH_CREATE_SPANS', () => tracingOps.batchCreateSpans(this.#client, this.#schema, args), {
      count: args.records.length,
    });
  }

  // -------------------------------------------------------------------------
  // Tracing — reads
  // -------------------------------------------------------------------------

  override async getSpan(args: GetSpanArgs): Promise<GetSpanResponse | null> {
    return this.#run('GET_SPAN', () => tracingOps.getSpan(this.#readClient, this.#schema, args), {
      traceId: args.traceId,
      spanId: args.spanId,
    });
  }

  override async getSpans(args: GetSpansArgs): Promise<GetSpansResponse> {
    return this.#run('GET_SPANS', () => tracingOps.getSpans(this.#readClient, this.#schema, args), {
      traceId: args.traceId,
      count: args.spanIds.length,
    });
  }

  override async getRootSpan(args: GetRootSpanArgs): Promise<GetRootSpanResponse | null> {
    return this.#run('GET_ROOT_SPAN', () => tracesOps.getRootSpan(this.#readClient, this.#schema, args), {
      traceId: args.traceId,
    });
  }

  override async getTrace(args: GetTraceArgs): Promise<GetTraceResponse | null> {
    return this.#run('GET_TRACE', () => tracingOps.getTrace(this.#readClient, this.#schema, args), {
      traceId: args.traceId,
    });
  }

  override async getTraceLight(args: GetTraceArgs): Promise<GetTraceLightResponse | null> {
    return this.#run('GET_TRACE_LIGHT', () => tracingOps.getTraceLight(this.#readClient, this.#schema, args), {
      traceId: args.traceId,
    });
  }

  override async listTraces(args: ListTracesArgs): Promise<ListTracesResponse> {
    return this.#run('LIST_TRACES', () => tracesOps.listTraces(this.#readClient, this.#schema, args));
  }

  override async queryTraces(plan: TrustedTraceQueryPlan): Promise<TraceQueryResponse> {
    return this.#run('QUERY_TRACES', () =>
      traceQueryOps.queryTraces(this.#readClient, this.#schema, plan, this.#traceQueryTimeoutMs),
    );
  }

  override async queryThreads(plan: TrustedThreadQueryPlan): Promise<QueryThreadsResult> {
    return this.#run('QUERY_THREADS', () =>
      traceQueryOps.queryThreads(this.#readClient, this.#schema, plan, this.#traceQueryTimeoutMs),
    );
  }

  override async listBranches(args: ListBranchesArgs): Promise<ListBranchesResponse> {
    return this.#run('LIST_BRANCHES', () => tracesOps.listBranches(this.#readClient, this.#schema, args));
  }

  // -------------------------------------------------------------------------
  // Logs / metrics / scores / feedback — writes
  // -------------------------------------------------------------------------

  override async batchCreateLogs(args: BatchCreateLogsArgs): Promise<void> {
    await this.#run('BATCH_CREATE_LOGS', () => logsOps.batchCreateLogs(this.#client, this.#schema, args), {
      count: args.logs.length,
    });
  }

  override async batchCreateMetrics(args: BatchCreateMetricsArgs): Promise<void> {
    await this.#run('BATCH_CREATE_METRICS', () => metricsOps.batchCreateMetrics(this.#client, this.#schema, args), {
      count: args.metrics.length,
    });
  }

  override async createScore(args: CreateScoreArgs): Promise<void> {
    await this.#run('CREATE_SCORE', () => scoresOps.createScore(this.#client, this.#schema, args));
  }

  override async batchCreateScores(args: BatchCreateScoresArgs): Promise<void> {
    await this.#run('BATCH_CREATE_SCORES', () => scoresOps.batchCreateScores(this.#client, this.#schema, args), {
      count: args.scores.length,
    });
  }

  override async createFeedback(args: CreateFeedbackArgs): Promise<void> {
    await this.#run('CREATE_FEEDBACK', () => feedbackOps.createFeedback(this.#client, this.#schema, args));
  }

  override async batchCreateFeedback(args: BatchCreateFeedbackArgs): Promise<void> {
    await this.#run('BATCH_CREATE_FEEDBACK', () => feedbackOps.batchCreateFeedback(this.#client, this.#schema, args), {
      count: args.feedbacks.length,
    });
  }

  // -------------------------------------------------------------------------
  // Scores / feedback — deletes
  // -------------------------------------------------------------------------

  override async deleteScores(args: DeleteScoresArgs): Promise<void> {
    await this.#run('DELETE_SCORES', () => scoresOps.deleteScores(this.#client, this.#schema, args), {
      count: args.scoreIds.length,
    });
  }

  override async deleteFeedback(args: DeleteFeedbackArgs): Promise<void> {
    await this.#run('DELETE_FEEDBACK', () => feedbackOps.deleteFeedback(this.#client, this.#schema, args), {
      count: args.feedbackIds.length,
    });
  }

  // -------------------------------------------------------------------------
  // Logs / metrics / scores / feedback — list reads
  // -------------------------------------------------------------------------

  override async listLogs(args: ListLogsArgs): Promise<ListLogsResponse> {
    return this.#run('LIST_LOGS', () => logsOps.listLogs(this.#readClient, this.#schema, args));
  }

  override async listMetrics(args: ListMetricsArgs): Promise<ListMetricsResponse> {
    return this.#run('LIST_METRICS', () => metricsOps.listMetrics(this.#readClient, this.#schema, args));
  }

  override async listScores(args: ListScoresArgs): Promise<ListScoresResponse> {
    return this.#run('LIST_SCORES', () => scoresOps.listScores(this.#readClient, this.#schema, args));
  }

  override async getScoreById(scoreId: string): Promise<ScoreRecord | null> {
    return this.#run('GET_SCORE_BY_ID', () => scoresOps.getScoreById(this.#readClient, this.#schema, scoreId), {
      scoreId,
    });
  }

  override async listFeedback(args: ListFeedbackArgs): Promise<ListFeedbackResponse> {
    return this.#run('LIST_FEEDBACK', () => feedbackOps.listFeedback(this.#readClient, this.#schema, args));
  }

  override async updateFeedbackReviewStatus(args: UpdateFeedbackReviewStatusArgs): Promise<FeedbackRecord> {
    return this.#run(
      'UPDATE_FEEDBACK_REVIEW_STATUS',
      () => feedbackOps.updateFeedbackReviewStatus(this.#client, this.#schema, args),
      { feedbackId: args.feedbackId },
    );
  }

  // -------------------------------------------------------------------------
  // OLAP — metrics
  // -------------------------------------------------------------------------

  override async getMetricAggregate(args: GetMetricAggregateArgs): Promise<GetMetricAggregateResponse> {
    return this.#run('GET_METRIC_AGGREGATE', () => metricsOps.getMetricAggregate(this.#readClient, this.#schema, args));
  }

  override async getMetricBreakdown(args: GetMetricBreakdownArgs): Promise<GetMetricBreakdownResponse> {
    return this.#run('GET_METRIC_BREAKDOWN', () => metricsOps.getMetricBreakdown(this.#readClient, this.#schema, args));
  }

  override async getMetricTimeSeries(args: GetMetricTimeSeriesArgs): Promise<GetMetricTimeSeriesResponse> {
    return this.#run('GET_METRIC_TIME_SERIES', () =>
      metricsOps.getMetricTimeSeries(this.#readClient, this.#schema, args),
    );
  }

  override async getMetricPercentiles(args: GetMetricPercentilesArgs): Promise<GetMetricPercentilesResponse> {
    return this.#run('GET_METRIC_PERCENTILES', () =>
      metricsOps.getMetricPercentiles(this.#readClient, this.#schema, args),
    );
  }

  // -------------------------------------------------------------------------
  // OLAP — scores
  // -------------------------------------------------------------------------

  override async getScoreAggregate(args: GetScoreAggregateArgs): Promise<GetScoreAggregateResponse> {
    return this.#run('GET_SCORE_AGGREGATE', () => scoresOps.getScoreAggregate(this.#readClient, this.#schema, args));
  }

  override async getScoreBreakdown(args: GetScoreBreakdownArgs): Promise<GetScoreBreakdownResponse> {
    return this.#run('GET_SCORE_BREAKDOWN', () => scoresOps.getScoreBreakdown(this.#readClient, this.#schema, args));
  }

  override async getScoreTimeSeries(args: GetScoreTimeSeriesArgs): Promise<GetScoreTimeSeriesResponse> {
    return this.#run('GET_SCORE_TIME_SERIES', () => scoresOps.getScoreTimeSeries(this.#readClient, this.#schema, args));
  }

  override async getScorePercentiles(args: GetScorePercentilesArgs): Promise<GetScorePercentilesResponse> {
    return this.#run('GET_SCORE_PERCENTILES', () =>
      scoresOps.getScorePercentiles(this.#readClient, this.#schema, args),
    );
  }

  // -------------------------------------------------------------------------
  // OLAP — feedback
  // -------------------------------------------------------------------------

  override async getFeedbackAggregate(args: GetFeedbackAggregateArgs): Promise<GetFeedbackAggregateResponse> {
    return this.#run('GET_FEEDBACK_AGGREGATE', () =>
      feedbackOps.getFeedbackAggregate(this.#readClient, this.#schema, args),
    );
  }

  override async getFeedbackBreakdown(args: GetFeedbackBreakdownArgs): Promise<GetFeedbackBreakdownResponse> {
    return this.#run('GET_FEEDBACK_BREAKDOWN', () =>
      feedbackOps.getFeedbackBreakdown(this.#readClient, this.#schema, args),
    );
  }

  override async getFeedbackTimeSeries(args: GetFeedbackTimeSeriesArgs): Promise<GetFeedbackTimeSeriesResponse> {
    return this.#run('GET_FEEDBACK_TIME_SERIES', () =>
      feedbackOps.getFeedbackTimeSeries(this.#readClient, this.#schema, args),
    );
  }

  override async getFeedbackPercentiles(args: GetFeedbackPercentilesArgs): Promise<GetFeedbackPercentilesResponse> {
    return this.#run('GET_FEEDBACK_PERCENTILES', () =>
      feedbackOps.getFeedbackPercentiles(this.#readClient, this.#schema, args),
    );
  }

  // -------------------------------------------------------------------------
  // Discovery
  // -------------------------------------------------------------------------

  override async getEntityTypes(args: GetEntityTypesArgs): Promise<GetEntityTypesResponse> {
    return this.#run('GET_ENTITY_TYPES', () =>
      discoveryOps.getEntityTypes(this.#client, this.#schema, args, this.#discovery),
    );
  }

  override async getEntityNames(args: GetEntityNamesArgs): Promise<GetEntityNamesResponse> {
    return this.#run('GET_ENTITY_NAMES', () =>
      discoveryOps.getEntityNames(this.#client, this.#schema, args, this.#discovery),
    );
  }

  override async getServiceNames(args: GetServiceNamesArgs): Promise<GetServiceNamesResponse> {
    return this.#run('GET_SERVICE_NAMES', () =>
      discoveryOps.getServiceNames(this.#client, this.#schema, args, this.#discovery),
    );
  }

  override async getEnvironments(args: GetEnvironmentsArgs): Promise<GetEnvironmentsResponse> {
    return this.#run('GET_ENVIRONMENTS', () =>
      discoveryOps.getEnvironments(this.#client, this.#schema, args, this.#discovery),
    );
  }

  override async getTags(args: GetTagsArgs): Promise<GetTagsResponse> {
    return this.#run('GET_TAGS', () => discoveryOps.getTags(this.#client, this.#schema, args, this.#discovery));
  }

  override async getMetricNames(args: GetMetricNamesArgs): Promise<GetMetricNamesResponse> {
    return this.#run('GET_METRIC_NAMES', () =>
      discoveryOps.getMetricNames(this.#client, this.#schema, args, this.#discovery),
    );
  }

  override async getMetricLabelKeys(args: GetMetricLabelKeysArgs): Promise<GetMetricLabelKeysResponse> {
    return this.#run('GET_METRIC_LABEL_KEYS', () =>
      discoveryOps.getMetricLabelKeys(this.#client, this.#schema, args, this.#discovery),
    );
  }

  override async getMetricLabelValues(args: GetMetricLabelValuesArgs): Promise<GetMetricLabelValuesResponse> {
    return this.#run('GET_METRIC_LABEL_VALUES', () =>
      discoveryOps.getMetricLabelValues(this.#client, this.#schema, args, this.#discovery),
    );
  }

  // -------------------------------------------------------------------------
  // Tracing — deletes / clear
  // -------------------------------------------------------------------------

  override async batchDeleteTraces(args: BatchDeleteTracesArgs): Promise<void> {
    await this.#run('BATCH_DELETE_TRACES', () => tracingOps.batchDeleteTraces(this.#client, this.#schema, args), {
      count: args.traceIds.length,
    });
  }

  override async dangerouslyClearAll(): Promise<void> {
    try {
      // Iterate ALL_SIGNAL_TABLES so a future signal added to the constant
      // is truncated automatically. Tracing has its own helper that runs the
      // span TRUNCATE; we skip it here to avoid running it twice.
      //
      // `RESTART IDENTITY` resets every owned sequence (notably `cursorId`
      // bigserials) so tests that clear between cases and then exercise
      // delta polling don't see surprising high-water-mark cursors. Without
      // it, sequences continue from where they left off across clears.
      await tracingOps.dangerouslyClearTracing(this.#client, this.#schema);
      for (const t of ALL_SIGNAL_TABLES) {
        if (t === TABLE_SPAN_EVENTS) continue;
        await this.#client.none(`TRUNCATE TABLE ${qualifiedTable(this.#schema, t)} RESTART IDENTITY`);
      }
      await this.#client.none(`TRUNCATE TABLE ${qualifiedTable(this.#schema, TABLE_DISCOVERY)} RESTART IDENTITY`);
    } catch (error) {
      wrapError('DANGEROUSLY_CLEAR_ALL', error);
    }
  }
}
